import * as React from 'react';

import { Autocomplete, TextField } from '@mui/material';
import { FormikProps } from 'formik';
import countryList from 'react-select-country-list';

export interface BillingDetailsValues {
  address1: string;
  address2: string;
  city: string;
  zipCode: string;
  state: string;
  country: string;
}

/**
 * Validates only the fields for this billing details form
 * @param values the values to validate
 * @returns an object where the keys correspond to the validated value keys
 * and their values are their error messages
 */
export const validateBillingDetails = (values: BillingDetailsValues) => {
  const errors: Partial<Record<keyof BillingDetailsValues, string>> = {};
  if (!values.address1) errors.address1 = 'Required';
  if (!values.city) errors.city = 'Required';
  if (!values.zipCode) errors.zipCode = 'Required';
  if (!values.country) errors.country = 'Required';
  if (!values.state && values.country === 'US') errors.state = 'Required';
  return errors;
};

export const initialBillingDetails: BillingDetailsValues = {
  address1: '',
  address2: '',
  city: '',
  zipCode: '',
  state: '',
  country: 'US',
};

/**
 * Renders the uncontrolled Formik form entering billing details
 */
const BillingDetails: React.FC<FormikProps<BillingDetailsValues>> = (props) => {
  const { values, errors, handleChange, handleBlur, touched, setFieldValue } = props;

  const countryOptions = React.useMemo(() => countryList().getData(), []);

  return (
    <div className="w-full">
      <h3 className="font-bold mb-4">Billing Details</h3>
      <div className="flex flex-col space-y-2">
        <h4 className="font-medium">Address line 1 *</h4>
        <TextField
          name="address1"
          value={values.address1}
          onChange={handleChange}
          variant="outlined"
          placeholder="Address line 1"
          fullWidth
          onBlur={handleBlur}
          error={touched.address1 && Boolean(errors.address1)}
          helperText={touched.address1 && errors.address1}
        />
        <h4 className="font-medium">Address line 2</h4>
        <TextField
          name="address2"
          value={values.address2}
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          placeholder="Address line 2"
          fullWidth
        />
        <div className="flex space-x-4">
          <div className="flex flex-col space-y-2 w-1/2">
            <h4 className="font-medium">City *</h4>
            <TextField
              name="city"
              value={values.city}
              onBlur={handleBlur}
              onChange={handleChange}
              variant="outlined"
              placeholder="City"
              fullWidth
              error={touched.city && Boolean(errors.city)}
              helperText={touched.city && errors.city}
            />
          </div>
          <div className="flex flex-col space-y-2 w-1/2">
            <h4 className="font-medium">ZIP Code *</h4>
            <TextField
              name="zipCode"
              value={values.zipCode}
              onChange={handleChange}
              onBlur={handleBlur}
              type="number"
              variant="outlined"
              placeholder="ZIP"
              fullWidth
              error={touched.zipCode && Boolean(errors.zipCode)}
              helperText={touched.zipCode && errors.zipCode}
            />
          </div>
        </div>
        <div className="flex space-x-4">
          <div className="flex flex-col space-y-2 w-1/2">
            <h4 className="font-medium">State *</h4>
            <div className="flex flex-col space-y-2">
              <TextField
                name="state"
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                placeholder="State"
                fullWidth
                error={touched.state && Boolean(errors.state)}
                helperText={touched.state && errors.state}
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2 w-1/2">
            <h4 className="font-medium">Country *</h4>
            <Autocomplete
              disablePortal
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={countryOptions}
              defaultValue={{ value: 'US', label: 'United States' }}
              onBlur={handleBlur}
              disableClearable
              onChange={(e, country) => setFieldValue('country', country && country.value)}
              renderInput={(params) => (
                <TextField
                  name="country"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  variant="outlined"
                  error={touched.country && Boolean(errors.country)}
                  helperText={touched.country && errors.country}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
BillingDetails.displayName = 'Checkout.BillingDetails';

export default BillingDetails;
